import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { toast } from 'sonner';
import Loading from '../components/Loading';
import { useHeader } from '@/contexts/HeaderContext';
import { deviceTimezone } from '@/lib/deviceTimezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditProfile = ({ userId }) => {
  const { t, i18n } = useTranslation();
  const isIt = i18n.language?.startsWith('it');
  const isEs = i18n.language?.startsWith('es');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const { setHeaderProps } = useHeader();
  useEffect(() => {
    setHeaderProps({ title: isIt ? 'Modifica profilo' : isEs ? 'Editar perfil' : 'Edit profile' });
  }, [setHeaderProps, isIt, isEs]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/user/${userId}`);
        setFormData(res.data);
      } catch (err) {
        console.error('Error fetching profile:', err);
        toast.error(isIt ? 'Errore nel caricamento' : isEs ? 'Error al cargar el perfil' : 'Error loading profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, isIt, isEs]);

  const handleChange = (field, value) => {
    const v = field === 'time_budget_minutes' && value !== '' ? Number(value) : value;
    setFormData(prev => ({ ...prev, [field]: v }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setErrors({ name: isIt ? 'Nome richiesto' : isEs ? 'Nombre obligatorio' : 'Name is required' });
      toast.error(isIt ? 'Compila i campi correttamente' : isEs ? 'Por favor completa los campos requeridos' : 'Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, time_budget_minutes: formData.time_budget_minutes !== undefined && formData.time_budget_minutes !== '' ? Number(formData.time_budget_minutes) : formData.time_budget_minutes };
      await axios.put(`${API}/user/${userId}`, payload);
      toast.success(isIt ? 'Profilo salvato!' : isEs ? '¡Perfil guardado!' : 'Profile saved!');
      navigate('/profile');
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error(isIt ? 'Errore nel salvataggio' : isEs ? 'Error al guardar el perfil' : 'Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  /* ── reusable styled pieces ──────────────────── */
  const SectionLabel = ({ children }) => (
    <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec, #7d7a75)', marginBottom: 10, fontWeight: 600, marginTop: 24 }}>
      {children}
    </div>
  );

  const CardWrap = ({ children }) => (
    <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, padding: '12px 16px', marginBottom: 4 }}>
      {children}
    </div>
  );

  const InputRow = ({ label, field, type = 'text', placeholder = '', required, disabled }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--c-texSec, #7d7a75)', marginBottom: 4, fontFamily: 'Manrope-Variable' }}>
        {label}{required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type}
        value={formData[field] ?? ''}
        onChange={e => handleChange(field, e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 12, border: errors[field] ? '1px solid #c0392b' : '1px solid rgba(255,255,255,0.08)',
          background: disabled ? 'var(--c-surface-card)' : 'var(--c-surface-elevated)', fontSize: 15, fontFamily: 'Manrope-Variable',
          color: 'var(--c-texPri)', outline: 'none', boxSizing: 'border-box',
          ...(disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {})
        }}
      />
      {errors[field] && <div style={{ fontSize: 11, color: '#c0392b', marginTop: 3, fontFamily: 'Manrope-Variable' }}>{errors[field]}</div>}
    </div>
  );

  const SelectRow = ({ label, field, options }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--c-texSec, #7d7a75)', marginBottom: 4, fontFamily: 'Manrope-Variable' }}>{label}</label>
      <select
        value={formData[field] ?? ''}
        onChange={e => handleChange(field, e.target.value)}
        style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid var(--c-border)', background: 'var(--c-surface-elevated)', fontSize: 15, fontFamily: 'Manrope-Variable', color: 'var(--c-texPri)', outline: 'none', boxSizing: 'border-box', appearance: 'auto' }}
      >
        <option value="">{isIt ? 'Seleziona...' : isEs ? 'Selecciona...' : 'Select...'}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const ChipSelect = ({ label, field, options, max = 3 }) => {
    const selected = formData[field] || [];
    return (
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--c-texSec, #7d7a75)', marginBottom: 6, fontFamily: 'Manrope-Variable' }}>
          {label}
          <span style={{ fontWeight: 400, marginLeft: 6 }}>({isIt ? `max ${max}` : `max ${max}`})</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {options.map(o => {
            const active = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  if (active) handleChange(field, selected.filter(v => v !== o.value));
                  else if (selected.length < max) handleChange(field, [...selected, o.value]);
                }}
                style={{
                  padding: '8px 14px', borderRadius: 20, border: 'none', fontSize: 13, fontFamily: 'Manrope-Variable', cursor: 'pointer',
                  background: active ? 'var(--c-btn-done-start)' : 'var(--c-surface-elevated)', color: active ? '#fff' : 'var(--c-texPri)',
                  fontWeight: active ? 600 : 400, transition: 'all 0.15s'
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── options matching onboarding ─────────────── */
  const relationshipOpts = [
    { value: 'married', label: isIt ? 'Sposati' : isEs ? 'Casados' : 'Married' },
    { value: 'living_together', label: isIt ? 'Conviventi' : isEs ? 'Conviviendo' : 'Living together' },
    { value: 'in_relationship', label: isIt ? 'In una relazione' : isEs ? 'En una relación' : 'In a relationship' },
  ];
  const motivationOpts = [
    { value: 'improve_relationship', label: isIt ? 'Migliorare la relazione' : isEs ? 'Mejorar la relación' : 'Improve relationship' },
    { value: 'be_present', label: isIt ? 'Essere più presente' : isEs ? 'Estar más presente' : 'Be more present' },
    { value: 'do_more', label: isIt ? 'Fare di più' : isEs ? 'Hacer más' : 'Do more' },
    { value: 'reconnect', label: isIt ? 'Riconnettersi' : isEs ? 'Reconectar' : 'Reconnect' },
  ];
  const workOpts = [
    { value: 'freelance', label: 'Freelance' },
    { value: 'part_time', label: 'Part-time' },
    { value: 'full_time', label: 'Full-time' },
    { value: 'entrepreneur', label: isIt ? 'Imprenditore' : isEs ? 'Emprendedor' : 'Entrepreneur' },
    { value: 'other', label: isIt ? 'Altro' : isEs ? 'Otro' : 'Other' },
  ];
  const ageOpts = [
    { value: '18-25', label: '18–25' },
    { value: '26-35', label: '26–35' },
    { value: '36-45', label: '36–45' },
    { value: '46+', label: '46+' },
  ];
  const timeOpts = [
    { value: 3, label: isIt ? '3 minuti' : isEs ? '3 minutos' : '3 min' },
    { value: 5, label: isIt ? '5 minuti' : isEs ? '5 minutos' : '5 min' },
    { value: 10, label: isIt ? '10 minuti' : isEs ? '10 minutos' : '10 min' },
    { value: -1, label: isIt ? 'Dipende dal giorno' : isEs ? 'Depende del día' : 'Depends on the day' },
  ];
  const improvementOpts = [
    { value: 'quality_time', label: isIt ? 'Tempo di qualità' : isEs ? 'Tiempo de calidad' : 'Quality time' },
    { value: 'home_management', label: isIt ? 'Gestione casa' : isEs ? 'Gestión del hogar' : 'Home management' },
    { value: 'emotional_presence', label: isIt ? 'Presenza emotiva' : isEs ? 'Presencia emocional' : 'Emotional presence' },
    { value: 'reduce_tension', label: isIt ? 'Ridurre tensione' : isEs ? 'Reducir tensión' : 'Reduce tension' },
    { value: 'confidence', label: isIt ? 'Sicurezza' : isEs ? 'Confianza' : 'Confidence' },
  ];
  const timezoneOpts = [
    { value: 'Europe/Rome', label: '🇮🇹 Italia (Roma)' },
    { value: 'Europe/London', label: '🇬🇧 UK (London)' },
    { value: 'Europe/Paris', label: '🇫🇷 France (Paris)' },
    { value: 'Europe/Berlin', label: '🇩🇪 Germany (Berlin)' },
    { value: 'Europe/Madrid', label: '🇪🇸 Spain (Madrid)' },
    { value: 'Europe/Zurich', label: '🇨🇭 Switzerland (Zurich)' },
    { value: 'America/New_York', label: '🇺🇸 USA East (New York)' },
    { value: 'America/Los_Angeles', label: '🇺🇸 USA West (Los Angeles)' },
    { value: 'America/Chicago', label: '🇺🇸 USA Central (Chicago)' },
    { value: 'America/Sao_Paulo', label: '🇧🇷 Brazil (São Paulo)' },
    { value: 'Australia/Sydney', label: '🇦🇺 Australia (Sydney)' },
    { value: 'Asia/Tokyo', label: '🇯🇵 Japan (Tokyo)' },
    { value: 'Asia/Dubai', label: '🇦🇪 Dubai' },
  ];
  // Most of the world isn't in that list: offer the phone's zone and the stored one too.
  [deviceTimezone(), formData.timezone].forEach((zone) => {
    if (zone && !timezoneOpts.some((o) => o.value === zone)) {
      timezoneOpts.unshift({ value: zone, label: zone.replace(/_/g, ' ') });
    }
  });

  /* ── render ──────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container" style={{ background: 'inherit' }}>
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit', fontFamily: 'Manrope-Variable' }}>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: '0 24px', paddingBottom: 100 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 0 12px' }}>
          <button onClick={() => navigate('/profile')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <Icon icon="solar:alt-arrow-left-linear" style={{ width: 16, height: 16, color: 'var(--c-texPri, #191919)' }} />
            <span style={{ fontSize: 16, color: 'var(--c-texPri, #191919)' }}>{isIt ? 'Indietro' : isEs ? 'Atrás' : 'Back'}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 20px', borderRadius: 12, border: 'none', background: 'var(--c-btn-done-start)', color: '#fff',
              fontSize: 14, fontWeight: 600, fontFamily: 'Manrope-Variable', cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.5 : 1
            }}
          >
            {saving ? (isIt ? 'Salvataggio...' : isEs ? 'Guardando...' : 'Saving...') : (isIt ? 'Salva' : isEs ? 'Guardar' : 'Save')}
          </button>
        </div>

        {/* Personal information */}
        <SectionLabel>{isIt ? 'Informazioni personali' : isEs ? 'Información personal' : 'Personal information'}</SectionLabel>
        <CardWrap>
          <InputRow label={isIt ? 'Nome' : isEs ? 'Nombre' : 'Name'} field="name" placeholder={isIt ? 'Il tuo nome' : isEs ? 'Tu nombre' : 'Your name'} required />
          {formData.email && <InputRow label="Email" field="email" type="email" disabled />}
          <SelectRow label={isIt ? 'Fuso orario' : isEs ? 'Zona horaria' : 'Timezone'} field="timezone" options={timezoneOpts} />
        </CardWrap>

        {/* About you */}
        <SectionLabel>{isIt ? 'Su di te' : isEs ? 'Sobre ti' : 'About you'}</SectionLabel>
        <CardWrap>
          <SelectRow label={isIt ? 'Relazione' : isEs ? 'Relación' : 'Relationship'} field="relationship_type" options={relationshipOpts} />
          <SelectRow label={isIt ? 'Età' : isEs ? 'Edad' : 'Age'} field="age_cluster" options={ageOpts} />
          <SelectRow label={isIt ? 'Motivazione' : isEs ? 'Motivación' : 'Motivation'} field="motivation" options={motivationOpts} />
          <SelectRow label={isIt ? 'Lavoro' : isEs ? 'Trabajo' : 'Work'} field="work_schedule" options={workOpts} />
        </CardWrap>

        {/* Preferences */}
        <SectionLabel>{isIt ? 'Le tue preferenze' : isEs ? 'Tus preferencias' : 'Your preferences'}</SectionLabel>
        <CardWrap>
          <SelectRow label={isIt ? 'Tempo disponibile' : isEs ? 'Tiempo disponible' : 'Available time'} field="time_budget_minutes" options={timeOpts} />
          <ChipSelect label={isIt ? 'Aree di miglioramento' : isEs ? 'Áreas de mejora' : 'Improvement areas'} field="improvement_areas" options={improvementOpts} max={3} />
        </CardWrap>
      </div>
    </div>
  );
};

export default EditProfile;
